import { useAppSelector } from '../../../../store/redux/hooks';
import { RootState } from '../../../../store/redux/store';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  Text,
  Input,
  UnorderedList,
  ListItem,
  Flex,
  IconButton,
  useToast,
  Tooltip,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import CustomVisitUrlIcon from '../../../../theme/icons/CustomVisitUrlIcon';
import CustomCopyIcon from '../../../../theme/icons/CustomCopyIcon';
import CustomToast from '../../../../common/CustomToast';

const FormIsLiveModal = ({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) => {
  const navigate = useNavigate();
  const toast = useToast();
  const slug = useAppSelector((state: RootState) => state.form.slug);
  const formFields = useAppSelector(
    (state: RootState) => state.form.formFields
  );
  const queryParams = useAppSelector(
    (state: RootState) => state.form.queryParams
  );
  const hasLinkedFormFields = formFields.some((f) => f.id && f.id !== 'none');
  const queryParamsString =
    queryParams.length > 0
      ? `?${queryParams.map((param) => `${param.id}=yourValue`).join('&')}`
      : '';
  const formUrl = `${process.env.REACT_APP_FORMS_BASE_URL || window.env.REACT_APP_FORMS_BASE_URL}/${slug}${queryParamsString}`;
  const handleClose = () => {
    onClose();
    navigate('/pass-templates');
  };
  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg" isCentered>
      <ModalOverlay />
      <ModalContent p={6}>
        <ModalCloseButton />
        <ModalBody>
          <Text textStyle="heading4" textAlign="center">
            Form is live
          </Text>
          <Flex direction="column" gap={8} mt={2}>
            {!hasLinkedFormFields && (
              <Text textStyle="bodyFamilyRegular" fontSize="16px" mt={3}>
                This link allows users to download the pass without filling out
                a form. It supports unlimited downloads — ideal for posters, POS
                displays and events.
              </Text>
            )}
            <Flex direction="column" gap={2}>
              <Text textStyle="bodyFamilyMedium" fontSize="16px">
                Form URL
              </Text>
              <Flex direction="row" gap={2} alignItems="center">
                <Input
                  variant="readOnlyUrl"
                  bg="#F8F8F8"
                  id="formUrl"
                  border="1px solid #D0D5DD"
                  borderRadius="10px"
                  type="text"
                  value={formUrl}
                  isReadOnly
                />
                <Tooltip
                  label="Go to URL"
                  hasArrow
                  placement="top"
                  fontSize="12px"
                  bg="inkBlue"
                  color="white"
                  borderRadius="6px"
                  textAlign="center"
                  p="10px"
                >
                  <IconButton
                    icon={<CustomVisitUrlIcon boxSize="20px" />}
                    onClick={() => {
                      window.open(formUrl, '_blank');
                    }}
                    aria-label="Go to URL"
                    variant="ghost"
                    borderRadius="10px"
                    borderColor="#D0D5DD"
                    borderWidth="1px"
                    bg="#F8F8F8"
                    _hover={{
                      bg: '#EEEEEE',
                      '& path': { stroke: '#252525' },
                    }}
                  />
                </Tooltip>
                <Tooltip
                  label="Copy URL"
                  hasArrow
                  placement="top"
                  fontSize="12px"
                  bg="inkBlue"
                  color="white"
                  borderRadius="6px"
                  textAlign="center"
                  p="10px"
                >
                  <IconButton
                    icon={<CustomCopyIcon boxSize="20px" color="#989898" />}
                    onClick={() => {
                      navigator.clipboard.writeText(formUrl);
                      toast({
                        render: (props) => (
                          <CustomToast
                            status="success"
                            title="URL copied"
                            description=""
                            onClose={props.onClose}
                            position="top"
                            duration={2000}
                            id={props.id}
                          />
                        ),
                      });
                    }}
                    cursor="pointer"
                    aria-label="Copy URL"
                    variant="ghost"
                    borderRadius="10px"
                    borderColor="#D0D5DD"
                    borderWidth="1px"
                    bg="#F8F8F8"
                    _hover={{
                      bg: '#EEEEEE',
                      '& path': { stroke: '#252525' },
                    }}
                  />
                </Tooltip>
              </Flex>
            </Flex>
            <Flex direction="column" gap={2}>
              <Text textStyle="bodyFamilyMedium" fontSize="16px">
                What to do next
              </Text>
              <UnorderedList spacing="5px">
                <ListItem>
                  <Text textStyle="bodyFamilyRegular" fontSize="16px">
                    Share this link directly with customers or
                  </Text>
                </ListItem>
                <ListItem>
                  <Text textStyle="bodyFamilyRegular" fontSize="16px">
                    Download a QR code to include on a website, email, or
                    digital message
                  </Text>
                </ListItem>
              </UnorderedList>
            </Flex>
          </Flex>
        </ModalBody>
        <ModalFooter justifyContent="center" mt={4}>
          <Button variant="gradient" onClick={handleClose} w="150px">
            Done
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default FormIsLiveModal;
